// Offline play's server state (spec/05 §8.4a, §8.5; offline play, 2026-10-01), as TanStack Query hooks over the
// generated client — `packages/contracts/src/routes/offline-play.ts`:
//
//   GET  …/venues/{v}/offline-staff-codes                   the venue's staff codes (never the digits)  sessions:read
//   POST …/venues/{v}/offline-staff-codes                   issue codes; the digits come back once      sessions:offline_code
//   POST …/venues/{v}/offline-staff-codes/{id}/revoke       revoke an unused code                       sessions:offline_code
//   GET  …/venues/{v}/offline-sessions[?status=]            sessions the venue's PCs ran offline        sessions:read
//   POST …/venues/{v}/offline-sessions/{id}/resolve         settle one: paid at the desk, or comp       sessions:offline_code

import type { IssueOfflineStaffCodes, IssuedOfflineStaffCodes, OfflineSession, OfflineStaffCode } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const offlineKeys = {
  codes: (organizationId: string, venueId: string) => ['offline-play', organizationId, venueId, 'codes'] as const,
  sessions: (organizationId: string, venueId: string) => ['offline-play', organizationId, venueId, 'sessions'] as const,
};

export function useOfflineStaffCodes(organizationId: string, venueId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: offlineKeys.codes(organizationId, venueId),
    queryFn: async (): Promise<readonly OfflineStaffCode[]> =>
      (await api.listOfflineStaffCodes({ path: { organizationId, venueId } })).items,
    enabled,
  });
}

export function useOfflineSessions(organizationId: string, venueId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: offlineKeys.sessions(organizationId, venueId),
    queryFn: async (): Promise<readonly OfflineSession[]> =>
      (await api.listOfflineSessions({ path: { organizationId, venueId }, query: {} })).items,
    enabled,
    refetchInterval: 30_000,
  });
}

/**
 * The sessions PCs ran offline that staff must still settle (`status=needs_attention`): the Floor's "What needs you"
 * counts them and links to Offline play (spec/13 §3.2). Settling one there re-reads this too (the same key prefix).
 */
export function useOfflineSessionsToSettle(organizationId: string, venueId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: [...offlineKeys.sessions(organizationId, venueId), 'needs_attention'] as const,
    queryFn: async (): Promise<readonly OfflineSession[]> =>
      (await api.listOfflineSessions({ path: { organizationId, venueId }, query: { status: 'needs_attention' } })).items,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useIssueOfflineStaffCodes(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: IssueOfflineStaffCodes): Promise<IssuedOfflineStaffCodes> => api.issueOfflineStaffCodes({ path: { organizationId, venueId }, body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: offlineKeys.codes(organizationId, venueId) }),
  });
}

export function useRevokeOfflineStaffCode(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (codeId: string): Promise<OfflineStaffCode> => api.revokeOfflineStaffCode({ path: { organizationId, venueId, codeId } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: offlineKeys.codes(organizationId, venueId) }),
  });
}

export function useResolveOfflineSession(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { readonly offlineSessionId: string; readonly resolution: 'paid_at_desk' | 'comp'; readonly note?: string }): Promise<OfflineSession> =>
      api.resolveOfflineSession({
        path: { organizationId, venueId, offlineSessionId: input.offlineSessionId },
        body: { resolution: input.resolution, ...(input.note === undefined ? {} : { note: input.note }) },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: offlineKeys.sessions(organizationId, venueId) }),
  });
}
